import { useEffect, useRef } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'
import { isTerminalBlastState, type EmailBlastState } from '@/types/emailBlast'

/**
 * Payload of the API's "BlastUpdated" SignalR event (ORB-706), broadcast to workspace-{workspaceId}
 * after a blast tick actually persisted. Deliberately carries no figures: `SignalRSubscribe`
 * access-checks only the `workspace-` prefix, so the payload holds nothing a joiner could not already
 * ask the API for. Treat it as "something changed, go and read it", never as data.
 */
export interface BlastUpdatedEvent {
  blastId: string
  state: EmailBlastState
}

/**
 * Refetches the open blast when its engine tick reports movement.
 *
 * Scoped to the workspace group rather than a per-blast one because that is the only group name the
 * subscribe endpoint actually access-checks; the blastId filter below is what narrows it. Several
 * blasts can run in one workspace, so without that filter every viewer would refetch on every other
 * blast's tick.
 *
 * Nothing here needs to stop the stream: a blast leaves the engine's active query the moment it reaches
 * a terminal state, so the events stop arriving on their own. The terminal check is belt-and-braces
 * against a late duplicate.
 */
export function useBlastLive(workspaceId: string, blastId: string, onUpdate: () => void): void {
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  useEffect(() => {
    if (!workspaceId || !blastId) return
    const group = `workspace-${workspaceId}`
    let cancelled = false

    const onEvent = (payload: unknown) => {
      const event = payload as BlastUpdatedEvent | null
      if (!event || event.blastId !== blastId) return
      if (event.state && isTerminalBlastState(event.state)) {
        // Still refetch: the transition INTO a terminal state is the one an operator most wants to see
        // land without a manual refresh. It is simply the last event this blast will ever send.
        onUpdateRef.current()
        return
      }
      onUpdateRef.current()
    }

    signalRService
      .joinGroup(group)
      .then(() => {
        if (cancelled) {
          signalRService.leaveGroup(group)
          return
        }
        signalRService.on('BlastUpdated', onEvent)
      })
      .catch(() => {
        /* A dropped realtime connection degrades to the page's own Refresh control and the staleness
           warning on the status spine, which is what that warning is there to cover. */
      })

    return () => {
      cancelled = true
      signalRService.off('BlastUpdated', onEvent)
      signalRService.leaveGroup(group)
    }
  }, [workspaceId, blastId])
}
